import { useEffect, useState } from 'react';
import { Modal, Button, Select } from 'antd';
import { getRegionList } from '@@service/api';
import './index.less';

export interface CountryOption {
    key: string;
    code: string;
    name: string;
}

interface CountrySelectorProps {
    open: boolean;
    onConfirm: (country: CountryOption) => void;
    urlCountryCode?: string;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
    open,
    onConfirm,
    urlCountryCode
}) => {
    const [ regions, setRegions ] = useState<CountryOption[]>([]);
    const [ selected, setSelected ] = useState<string | undefined>(undefined);
    const [ loading, setLoading ] = useState(false);
    const [ title, setTitle ] = useState('');
    const [ subTitle, setSubTitle ] = useState('');
    const [ otherNotSupportTips, setOtherNotSupportTips ] = useState('');
    const [ otherNotSupportLink, setOtherNotSupportLink ] = useState('');

    useEffect(() => {
        if (!open) return;
        setLoading(true);
        getRegionList().then(data => {
            const list = data.regions || [];
            setRegions(list);
            setTitle(data.title || '');
            setSubTitle(data.subTitle || '');
            setOtherNotSupportTips(data.otherNotSupportTips || '');
            setOtherNotSupportLink(data.otherNotSupportLink || '');

            // 默认值：优先 URL 参数 countryCode → 匹配 regions 中的 key/code
            if (urlCountryCode) {
                const matched = list.find(r => r.key === urlCountryCode || r.code === urlCountryCode);
                if (matched) {
                    setSelected(matched.key);
                } else {
                    // URL 参数匹配不到 → 默认选 "other"
                    const otherItem = list.find(r => r.key === 'other' || r.code === 'other');
                    setSelected(otherItem ? otherItem.key : undefined);
                }
            } else {
                // URL 参数没传 → 使用后端 default_key
                if (data.default_key) {
                    setSelected(data.default_key);
                } else {
                    const otherItem = list.find(r => r.key === 'other' || r.code === 'other');
                    setSelected(otherItem ? otherItem.key : undefined);
                }
            }
        }).catch(() => {
            setRegions([]);
        }).finally(() => {
            setLoading(false);
        });
    }, [ open, urlCountryCode ]);

    const handleConfirm = () => {
        if (!selected) return;
        const country = regions.find(c => c.key === selected);
        if (!country) return;

        // 选的是 "other" → 跳转到不支持链接
        if (country.code === 'other' && otherNotSupportLink) {
            window.location.href = otherNotSupportLink;
            return;
        }

        onConfirm(country);
    };

    const selectOptions = regions.map(r => ({ label: r.name, value: r.key }));
    const isOther = selected ? regions.find(r => r.key === selected)?.code === 'other' : false;

    return (
        <Modal
            open={open}
            footer={null}
            closable={false}
            maskClosable={false}
            keyboard={false}
            centered
            wrapClassName="country-selector-modal"
            title={title || 'Select Your Region'}
        >
            <p className="country-selector-desc">
                {subTitle || 'Please select your region to continue.'}
            </p>
            <Select
                className="country-selector-select"
                placeholder="Select Your Region"
                aria-label="Select Your Region"
                value={selected}
                onChange={setSelected}
                options={selectOptions}
                loading={loading}
                classNames={{ popup: { root: 'country-selector-dropdown' } }}
            />
            {isOther && otherNotSupportTips && (
                <p className="country-selector-warning">{otherNotSupportTips}</p>
            )}
            <Button
                type="primary"
                className="country-selector-btn"
                onClick={handleConfirm}
                disabled={!selected}
                block
            >
                Continue
            </Button>
        </Modal>
    );
};

export default CountrySelector;
